const sheetCache = new Map<string, CSSStyleSheet>();

export type CombatStyles = CSSStyleSheet | CSSStyleSheet[] | string | string[];

export function cssStyleSheet(cssText: string): CSSStyleSheet {
  let sheet = sheetCache.get(cssText);

  if (!sheet) {
    sheet = new CSSStyleSheet();
    sheet.replaceSync(cssText);
    sheetCache.set(cssText, sheet);
  }

  return sheet;
}

export class CombatElement extends HTMLElement {
  static readonly styles: CombatStyles = [];
  public static readonly tagName: string;

  private rendered: boolean = false;
  private listenerController: AbortController | null = null;

  constructor() {
    super();

    if (!this.shadowRoot) {
      this.attachShadow({ mode: "open" });
    }
  }

  /**
   * Returns a fresh `AbortSignal` for binding event listeners, aborting any
   * listeners registered via a previous call. The signal is aborted
   * automatically when the element is disconnected, so listeners bound with it
   * need no manual teardown. Subclasses that override `disconnectedCallback`
   * for their own cleanup must call `super.disconnectedCallback()`.
   */
  protected freshSignal(): AbortSignal {
    this.listenerController?.abort();
    this.listenerController = new AbortController();
    return this.listenerController.signal;
  }

  disconnectedCallback(): void {
    this.listenerController?.abort();
    this.listenerController = null;
  }

  protected renderTemplate(html: string): void {
    this.adoptStyles();
    if (!this.shadowRoot || this.rendered) {
      return;
    }
    this.rendered = true;
    this.appendShadowTemplate(html);
  }

  protected stampTemplate(html: string): void {
    this.adoptStyles();
    this.rendered = true;
    this.appendShadowTemplate(html);
  }

  protected setNullableAttribute(
    name: string,
    value: string | number | null,
  ): void {
    if (value === null || value === "") {
      this.removeAttribute(name);
    } else {
      this.setAttribute(name, String(value));
    }
  }

  protected setCssVar(variable: string, value: string | null): void {
    if (value === null) {
      this.style.removeProperty(variable);
    } else {
      this.style.setProperty(variable, value);
    }
  }

  private adoptStyles(): void {
    const root = this.shadowRoot;
    if (!root || root.adoptedStyleSheets.length > 0) return;

    const styles = (this.constructor as typeof CombatElement).styles;
    const list = Array.isArray(styles) ? styles : [styles];
    root.adoptedStyleSheets = list.map((s) =>
      typeof s === "string" ? cssStyleSheet(s) : s,
    );
  }

  private appendShadowTemplate(html: string): void {
    if (!this.shadowRoot) {
      return;
    }

    const template = document.createElement("template");
    template.innerHTML = html;
    this.shadowRoot.appendChild(template.content.cloneNode(true));
  }
}

export interface CombatElementConstructor {
  new (): CombatElement;
  readonly tagName: string;
}

export function defineElement(
  ctor: CombatElementConstructor,
  registry: CustomElementRegistry = customElements,
): void {
  if (!registry.get(ctor.tagName)) {
    registry.define(ctor.tagName, ctor);
  }
}
